import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MathText } from '../../components/MathText';
import { PageFrame } from '../../components/PageFrame';
import { ReadAloudButton } from './ReadAloudButton';
import { canReadAloud, spokenWords } from './readAloud';
import { SafeExitLink } from './SafeExitLink';
import type { createLearnerSessionClient } from '../../platform/learner/learner-session-client';

// Same calm, blame-free discipline as PracticeTask/StudentStart: never
// technical detail, never implication of something this pack doesn't build
// (e.g. real-time paging of an adult).
const GENERIC_FALLBACK =
  "Take your time. Read the question again slowly, or ask an adult nearby if you'd like help.";
const NOT_SAVED_NOTE = 'This might not have gone through — you can try again.';
// UX audit: "recorded" reads clinical/surveillance-like to a child who
// just admitted confusion. Same honest meaning, warmer wording.
const NEED_AN_ADULT_CONFIRMATION =
  'Your teacher will know to check in with you.';
const NEED_AN_ADULT_NOT_SAVED_NOTE =
  "This might not have gone through — you can try 'I need an adult' again.";
const READY_TO_CONTINUE_NOT_SAVED_NOTE =
  'That might not have gone through — you can try again.';

type Props = {
  client: Pick<
    ReturnType<typeof createLearnerSessionClient>,
    'getCurrentPractice' | 'sendAction'
  >;
};

// Defensive read, same discipline as PracticeTask's content.prompt handling:
// only trust content.support/content.hint if content is an object and the
// field is actually a string. Never invent per-item support content.
function readSupportText(content: unknown): string | null {
  if (typeof content !== 'object' || content === null) return null;
  const record = content as Record<string, unknown>;
  if (typeof record.support === 'string') return record.support;
  if (typeof record.hint === 'string') return record.hint;
  // Stop-gap (2026-09-28) until every question carries its own hint: the
  // Years 1-6 questions' "format" line often describes a method ("Count how
  // many groups of 5 fit into 45.") -- show that, but not the ones that
  // only say how to type the answer ("Enter a number").
  if (typeof record.format === 'string' && isMethodNote(record.format))
    return record.format;
  return null;
}

function isMethodNote(format: string): boolean {
  const text = format.trim();
  return (
    text.length > 0 &&
    !/^(enter|give|type|provide|write|answer|round|use)\b/i.test(text)
  );
}

export function HelpState({ client }: Props) {
  const navigate = useNavigate();
  const [supportText, setSupportText] = useState<string | null>(null);
  const [helpRequestFailed, setHelpRequestFailed] = useState(false);
  const [needsAnAdultConfirmed, setNeedsAnAdultConfirmed] = useState(false);
  const [needAnAdultFailed, setNeedAnAdultFailed] = useState(false);
  const [readyToContinueFailed, setReadyToContinueFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;

    // Mount is the point the help request is actually recorded — a learner
    // who navigates here and immediately backs out has recorded a request,
    // per Task 2/3's design. This call's own failure must not block the
    // calm support content from rendering.
    client.sendAction('help_requested', {}, crypto.randomUUID()).catch(() => {
      if (active) setHelpRequestFailed(true);
    });

    client
      .getCurrentPractice()
      .then((result) => {
        if (!active) return;
        setSupportText(readSupportText(result.currentItem?.content ?? null));
      })
      .catch(() => {
        // Support content is a nice-to-have on this screen; if it can't be
        // fetched, the generic calming fallback still renders.
        if (active) setSupportText(null);
      });

    return () => {
      active = false;
    };
  }, [client]);

  async function handleReadyToContinue() {
    // help_resolved's SQL guard only succeeds from status = 'help_requested'
    // (or idempotently from 'in_progress'). If the mount-time
    // help_requested call itself failed, the session was never actually
    // moved into 'help_requested' — it's still whatever it was before
    // (typically 'started', if Help was reached before the first answer).
    // Calling help_resolved in that case has nothing to resolve and always
    // raises ACTION_NOT_ALLOWED, no matter how many times it's retried —
    // a permanent dead end with no way back to the task. When we already
    // know the request was never recorded, skip straight to navigating
    // back rather than calling an action that cannot succeed.
    if (helpRequestFailed) {
      void navigate('/learn/practice', { replace: true });
      return;
    }
    setBusy(true);
    setReadyToContinueFailed(false);
    try {
      await client.sendAction('help_resolved', {}, crypto.randomUUID());
      void navigate('/learn/practice', { replace: true });
    } catch {
      // Success/failure must be distinguished, not silently assumed — same
      // discipline as the mount-time help_requested call. Stay on this
      // screen with the button available to retry, rather than navigating
      // on a call that may not have actually resolved help.
      setBusy(false);
      setReadyToContinueFailed(true);
    }
  }

  async function handleNeedAnAdult() {
    setBusy(true);
    setNeedAnAdultFailed(false);
    try {
      await client.sendAction(
        'report_problem',
        { category: 'need_an_adult' },
        crypto.randomUUID(),
      );
      setBusy(false);
      // need_an_adult takes priority over progression (controlling prompt
      // §8) — show the confirmation and let the learner choose what
      // happens next rather than auto-navigating anywhere. Only shown on
      // genuine success — a "calm, honest confirmation" per the plan must
      // not claim something was recorded when the call actually failed.
      setNeedsAnAdultConfirmed(true);
    } catch {
      setBusy(false);
      setNeedAnAdultFailed(true);
    }
  }

  return (
    <PageFrame title="Help" audience="learner">
      <p className="mt-4 text-lg leading-relaxed text-ink-soft">
        Asking for help is not a problem — it's part of learning.
      </p>
      {/* The hint is the actual help, so it gets the emphasis (it used to be
          the smallest, greyest line on the screen). */}
      <p className="mt-4 rounded-xl border border-forest-600/15 bg-sage px-4 py-3 text-xl leading-snug font-medium text-ink">
        <MathText text={supportText ?? GENERIC_FALLBACK} />
      </p>
      {/* Reads the hint aloud, as the question is read on the practice
          screen. Not shown where the browser cannot speak. */}
      {canReadAloud() && (
        <div className="mt-3">
          <ReadAloudButton
            text={spokenWords(supportText ?? GENERIC_FALLBACK)}
          />
        </div>
      )}

      {needsAnAdultConfirmed && (
        <p
          role="status"
          className="mt-6 rounded-xl border border-forest-600/15 bg-sage p-4 text-forest-800"
        >
          {NEED_AN_ADULT_CONFIRMATION}
        </p>
      )}

      {helpRequestFailed && (
        <p className="mt-4 text-sm text-muted">{NOT_SAVED_NOTE}</p>
      )}
      {needAnAdultFailed && (
        <p role="alert" className="mt-4 text-sm text-muted">
          {NEED_AN_ADULT_NOT_SAVED_NOTE}
        </p>
      )}
      {readyToContinueFailed && (
        <p role="alert" className="mt-4 text-sm text-muted">
          {READY_TO_CONTINUE_NOT_SAVED_NOTE}
        </p>
      )}

      <div className="mt-6 grid gap-3 sm:flex sm:flex-wrap">
        <button
          type="button"
          disabled={busy}
          className="dm-btn-primary"
          onClick={() => {
            void handleReadyToContinue();
          }}
        >
          I'm ready to continue
        </button>
        {!needsAnAdultConfirmed && (
          <button
            type="button"
            disabled={busy}
            className="dm-btn-secondary"
            onClick={() => {
              void handleNeedAnAdult();
            }}
          >
            I need an adult
          </button>
        )}
      </div>

      <SafeExitLink />
    </PageFrame>
  );
}
